import React, { useState } from 'react';
import { DraggableProvidedDragHandleProps } from 'react-beautiful-dnd';
import { BsArrowsMove } from 'react-icons/bs';
import { FiChevronDown, FiChevronUp, FiPlus, FiTrash2 } from 'react-icons/fi';
import styled from 'styled-components';
import { RhButton, RhButtonMode } from '../../../core/RhButton';
import { RhCard } from '../../../core/RhCard';
import { colors, H2 } from '../../../core/Styles';

type Props = {
  title: string;
  isEditMode: boolean;
  dragHandleProps?: DraggableProvidedDragHandleProps;
  onAddButtonClick?: () => void;
  onDeleteButtonClick?: () => void;
  onChangeTitle?: (
    value: string,
    additionalData: {
      oldValue: string;
      resetValue: () => void;
    }
  ) => void;
};

/**
 * List of Bible Study Sections
 *
 * If you want to display modules within a section,
 * you should provide it in {children} manually.
 *
 * Example:
 * ```jsx
 * <StudySectionCard title="Sample">
 *  {modules.map(module => (
 *    <StudySectionModuleCard {...} />
 *  ))}
 * </StudySectionCard>
 * ```
 *
 * @param props
 * @returns
 */
export const StudySectionCard: React.FC<Props> = props => {
  const [isChildrenShown, setIsChildrenShown] = useState(false);
  return (
    <RhCard mode="tiled" background={colors.background}>
      <SectionHeader>
        <SectionTitle>
          <H2>{props.title}</H2>
        </SectionTitle>
        <SectionHeaderActions>
          <RhButton
            key="module-collapse"
            mode={RhButtonMode.subtle}
            prefixIcon={isChildrenShown ? FiChevronUp : FiChevronDown}
            iconSize={18}
            background="#fff"
            onClick={() => setIsChildrenShown(is => !is)}
          />
          {!!props.onDeleteButtonClick && (
            <RhButton
              key="module-del"
              mode={RhButtonMode.subtle}
              prefixIcon={FiTrash2}
              iconSize={18}
              background="#fff"
              onClick={props.onDeleteButtonClick}
            />
          )}
          {!!props.onAddButtonClick && (
            <RhButton
              key="module-add"
              mode={RhButtonMode.subtle}
              prefixIcon={FiPlus}
              iconSize={18}
              background="#fff"
              onClick={props.onAddButtonClick}
            />
          )}
          {!!props.dragHandleProps && (
            <div key="module-handle" style={styles.dragHandle} {...props.dragHandleProps}>
              <BsArrowsMove color={colors.lightGrey} size={20} />
            </div>
          )}
        </SectionHeaderActions>
      </SectionHeader>
      {isChildrenShown && props.children}
    </RhCard>
  );
};

const styles = {
  dragHandle: {
    display: 'flex',
  },
  titleInput: {
    backgroundColor: '#fff',
    borderColor: '#fff',
  },
};

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  & > :not(:last-child) {
    margin-right: 13px;
  }
`;

const SectionTitle = styled.div`
  flex: 1;
`;

const SectionHeaderActions = styled.div`
  display: flex;
  align-items: center;
  & > :not(:last-child) {
    margin-right: 13px;
  }
`;
